import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../lib/api';
import type { ProjectDto } from '../lib/types';
import { useAuth } from '../lib/auth';
import { useApiStatus, useFolderParam, useFormsOverview } from '../lib/workspace';
import { IconButton } from './ui';
import { Icon, type IconName } from './icons';
import { CompanySwitcher } from './CompanySwitcher';
import { NotificationBell } from './NotificationBell';
import { CommandPalette, cycleTheme, usePaletteShortcut } from './CommandPalette';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from './Menu';
import { useConfirm } from './Dialog';

/**
 * The app shell.
 *
 * A command bar over a single rail. The bar carries identity and state — which company,
 * how many forms, how many live — and nothing you navigate with; the rail carries all the
 * navigation there is.
 *
 * Folders live in that rail rather than in a second column inside the forms page. Two
 * rails side by side spent 400px before any content, and the folder you were in was
 * invisible from every other screen. The selection lives in the URL (see useFolderParam)
 * so the rail can set it without owning the list that reads it.
 *
 * On a form the rail collapses to icons: the builder is a three-column layout of its own
 * and a full sidebar competing with the palette makes the canvas unusably narrow.
 */
export function AppLayout() {
  const { auth, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [folder, setFolder] = useFolderParam();

  // Below the sm breakpoint the rail has nowhere to sit beside the content, so it opens
  // over it instead. Without this it was simply `hidden` with no control to bring it back,
  // which left a narrow window with no navigation at all — the logo was the only link out
  // of whatever page you had landed on.
  const [navOpen, setNavOpen] = useState(false);
  const { overview } = useFormsOverview();
  const status = useApiStatus();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  usePaletteShortcut(openPalette);

  const inBuilder = /^\/forms\/[^/]+/.test(location.pathname);
  const onForms = location.pathname === '/';

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects.list(),
    enabled: !inBuilder,
  });

  const folders = projects.data?.data ?? [];

  const workspaceNav: { to: string; label: string; icon: IconName; end?: boolean }[] = [
    { to: '/responses', label: 'Responses', icon: 'checkbox' },
    { to: '/organisation', label: 'Organisation', icon: 'sparkle' },
    { to: '/companies', label: 'Companies', icon: 'building' },
    { to: '/themes', label: 'Themes', icon: 'palette' },
    { to: '/team', label: 'Team', icon: 'users' },
    { to: '/api-keys', label: 'API keys', icon: 'ticket' },
    { to: '/audit', label: 'Audit log', icon: 'shield' },
  ];

  // Navigating is the point of the drawer, so following a link closes it.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const goToFolder = (value: string | null) => {
    if (onForms) setFolder(value);
    else navigate(value ? `/?folder=${encodeURIComponent(value)}` : '/');
  };

  return (
    <div className="flex h-full flex-col bg-[var(--ff-surface-sunken)]">
      <a href="#main" className="ff-sr focus:not-sr-only">Skip to main content</a>

      {/* ---- command bar -------------------------------------------------------- */}
      <header className="flex h-[52px] shrink-0 items-center gap-3.5 border-b
                         border-[var(--ff-border)] bg-[var(--ff-surface)] px-4">
        {/* The only way to reach the rail when it cannot sit beside the content. */}
        {!inBuilder && (
          <IconButton
            label={navOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={navOpen}
            aria-controls="main-nav"
            className="sm:hidden"
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon name={navOpen ? 'close' : 'menu'} />
          </IconButton>
        )}

        <Link to="/" className="flex shrink-0 items-center gap-2.5 font-semibold
                                tracking-[-0.01em]">
          <span className="grid h-5 w-5 place-items-center rounded-[5px]
                           bg-[var(--ff-accent)] text-[12px] font-bold text-[#2a2118]">
            F
          </span>
          <span className="hidden sm:inline">FormForge</span>
        </Link>

        {/* Tenant is the billing and isolation boundary; company is the tier inside it. */}
        <div className="hidden items-center gap-3 border-l border-[var(--ff-border)] pl-3.5
                        md:flex">
          <span className="max-w-[11rem] truncate text-[13.5px] text-[var(--ff-text-muted)]">
            {auth?.tenant.name}
          </span>
          <CompanySwitcher />
        </div>

        <button
          type="button"
          onClick={openPalette}
          className="mx-auto hidden max-w-[440px] flex-1 items-center gap-2.5 rounded-[7px]
                     border border-[var(--ff-border)] bg-[var(--ff-surface-sunken)] px-2.5
                     py-1.5 text-[13.5px] text-[var(--ff-text-muted)]
                     hover:border-[var(--ff-border-strong)] lg:flex"
        >
          <Icon name="search" size={14} />
          <span>Search forms or run a command</span>
          <kbd className="ff-mono ml-auto rounded border border-[var(--ff-border)]
                          bg-[var(--ff-surface-raised)] px-1.5 text-[11.5px]
                          text-[var(--ff-text-faint)]">
            ⌘K
          </kbd>
        </button>

        <div className="ml-auto flex items-center gap-4">
          <div className="hidden items-center gap-4 xl:flex">
            <Readout value={overview.forms} label="forms" />
            <Readout value={overview.live} label="live" />
            <Readout value={overview.draft} label="draft" tone="warn" />
            <Readout value={overview.responses} label={overview.responses === 1 ? 'response' : 'responses'} />
          </div>

          <div className="flex items-center gap-1">
            <IconButton label="Search forms or run a command" onClick={openPalette}
                        className="lg:hidden">
              <Icon name="search" />
            </IconButton>
            <NotificationBell />
            <NavLink
              to="/help"
              title="Help and documentation"
              aria-label="Help and documentation"
              className={({ isActive }) => clsx(
                'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                isActive
                  ? 'bg-[var(--ff-surface-raised)] text-[var(--ff-primary)]'
                  : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]',
              )}
            >
              <Icon name="help" />
            </NavLink>
            <IconButton label="Change colour theme" onClick={cycleTheme}>
              <Icon name="sun" />
            </IconButton>
          </div>

          <Menu
            label="Account menu"
            trigger={
              <span className="grid h-[26px] w-[26px] place-items-center rounded-full border
                               border-[var(--ff-border)] bg-[var(--ff-surface-raised)]
                               text-[12px] font-medium text-[var(--ff-text-muted)]
                               transition-colors hover:border-[var(--ff-text-faint)]
                               hover:text-[var(--ff-text)]">
                {(auth?.user.displayName ?? auth?.user.email ?? '?')[0]?.toUpperCase()}
              </span>
            }
          >
            {(close) => (
              <>
                <MenuLabel>{auth?.user.email}</MenuLabel>
                <MenuSeparator />
                <MenuItem icon="shield" onClick={() => { close(); navigate('/account'); }}>
                  Your account
                </MenuItem>
                <MenuItem icon="sun" onClick={() => { cycleTheme(); close(); }}>
                  Change colour theme
                </MenuItem>
                <MenuItem icon="help" onClick={() => { close(); navigate('/help'); }}>
                  Help and documentation
                </MenuItem>
                <MenuSeparator />
                <MenuItem icon="external" tone="danger" onClick={logout}>
                  Sign out
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* ---- rail ------------------------------------------------------------- */}
        {/* Dismisses the drawer without having to hit the small close button. Only ever
            present at the width where the rail overlays the content. */}
        {navOpen && !inBuilder && (
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setNavOpen(false)}
            className="fixed inset-0 z-30 bg-black/40 sm:hidden"
          />
        )}

        <nav
          id="main-nav"
          aria-label="Main"
          className={clsx(
            'shrink-0 overflow-y-auto border-r border-[var(--ff-border)]',
            'bg-[var(--ff-surface)] p-2.5',
            inBuilder
              ? 'w-14'
              : clsx(
                  'w-[196px] sm:static sm:block sm:shadow-none',
                  // Over the content, not beside it: at this width there is no room for a
                  // column, and squeezing one in leaves neither usable.
                  navOpen
                    ? 'fixed inset-y-0 left-0 z-40 block shadow-[var(--ff-shadow-lg)]'
                    : 'hidden',
                ),
          )}
        >
          {!inBuilder && (
            <>
              <FolderRail
                folders={folders}
                allCount={overview.forms}
                selected={onForms ? folder : undefined}
                onSelect={goToFolder}
              />

              <h2 className="ff-eyebrow mb-1.5 ml-2 mt-5">Workspace</h2>
            </>
          )}

          {inBuilder && (
            <NavLink
              to="/"
              end
              title="All forms"
              className="mb-1 flex items-center justify-center rounded-md px-2.5 py-2
                         text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)]
                         hover:text-[var(--ff-text)]"
            >
              <Icon name="grid" size={17} />
            </NavLink>
          )}

          {workspaceNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={inBuilder ? item.label : undefined}
              className={({ isActive }) => clsx(
                'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                inBuilder ? 'justify-center' : 'justify-start',
                isActive
                  ? 'bg-[var(--ff-surface-raised)] text-[var(--ff-text)] shadow-[inset_2px_0_0_var(--ff-accent)]'
                  : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]',
              )}
            >
              <Icon name={item.icon} size={inBuilder ? 17 : 15} />
              {!inBuilder && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <main id="main" className="min-h-0 min-w-0 flex-1 overflow-auto">
          {status.offline && (
            <OfflineBanner
              since={status.downSince}
              stale={status.hasStaleData}
              retrying={status.retrying}
              onRetry={status.retry}
            />
          )}
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

function Readout({
  value, label, tone,
}: { value: number; label: string; tone?: 'warn' }) {
  return (
    <p className="whitespace-nowrap text-[12.5px] text-[var(--ff-text-muted)]">
      <b className={clsx(
        'ff-num font-medium',
        tone === 'warn' ? 'text-[var(--ff-accent)]' : 'text-[var(--ff-text)]',
      )}>
        {value}
      </b>{' '}
      {label}
    </p>
  );
}

/**
 * Folders, in the rail.
 *
 * A flat list rather than a tree, even though folders can nest three deep: a form belongs
 * to exactly one folder, and what somebody wants here is "show me that one", not a
 * structure to navigate. Nesting shows as an indent rather than as something to expand —
 * collapsible branches hide the thing you are looking for behind a click.
 */
function FolderRail({
  folders, allCount, selected, onSelect,
}: {
  folders: ProjectDto[];
  allCount: number;
  /** undefined when the list is not on screen, so no row claims to be filtering. */
  selected: string | null | undefined;
  onSelect: (value: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  /** The folder currently being renamed in place, if any. */
  const [renaming, setRenaming] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    void queryClient.invalidateQueries({ queryKey: ['forms'] });
  };

  const create = useMutation({
    mutationFn: (value: string) => api.projects.create({ name: value }),
    onSuccess: () => { setName(''); setAdding(false); invalidate(); },
  });

  const remove = useMutation({
    mutationFn: (projectId: string) => api.projects.remove(projectId),
    onSuccess: (result) => {
      if (selected === result.id) onSelect(null);
      invalidate();
    },
  });

  const rename = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.projects.update(input.id, { name: input.name }),
    onSuccess: () => { setRenaming(null); invalidate(); },
  });

  const depthOf = (project: ProjectDto): number => {
    let depth = 0;
    let current = project.parentId;

    // Bounded rather than trusting the data: the server refuses cycles, and a rail that
    // hangs because one slipped through would be a worse failure than a wrong indent.
    while (current && depth < 4) {
      current = folders.find((f) => f.id === current)?.parentId ?? null;
      depth++;
    }

    return depth;
  };

  const ordered = [...folders].sort((a, b) => {
    const byDepth = depthOf(a) - depthOf(b);
    return byDepth !== 0 ? byDepth : a.name.localeCompare(b.name);
  });

  return (
    <>
      <div className="mb-1.5 ml-2 flex items-center justify-between gap-2 pr-0.5">
        <h2 className="ff-eyebrow">Folders</h2>
        <button
          type="button"
          aria-label="New folder"
          title="New folder"
          onClick={() => setAdding((v) => !v)}
          className="rounded p-0.5 text-[var(--ff-text-faint)] hover:text-[var(--ff-text)]"
        >
          <Icon name="plus" size={13} />
        </button>
      </div>

      {adding && (
        <form
          className="mb-1.5 px-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate(name.trim());
          }}
        >
          <input
            value={name}
            autoFocus
            placeholder="Folder name"
            aria-label="Folder name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setName(''); } }}
            className="w-full rounded-md border border-[var(--ff-border-strong)] bg-transparent
                       px-2 py-1 text-[13px] outline-none
                       placeholder:text-[var(--ff-text-faint)]"
          />
        </form>
      )}

      <RailRow
        label="All forms"
        count={allCount}
        active={selected === null}
        onClick={() => onSelect(null)}
      />

      {ordered.map((project) => (
        <RailRow
          key={project.id}
          label={project.name}
          count={project.formCount}
          colour={project.color}
          indent={depthOf(project)}
          active={selected === project.id}
          onClick={() => onSelect(project.id)}
          renaming={renaming === project.id}
          onRename={() => setRenaming(project.id)}
          onRenamed={(value) => {
            const trimmed = value.trim();
            if (!trimmed || trimmed === project.name) setRenaming(null);
            else rename.mutate({ id: project.id, name: trimmed });
          }}
          onCancelRename={() => setRenaming(null)}
          onDelete={async () => {
            const ok = await confirm({
              title: `Delete “${project.name}”?`,
              tone: 'danger',
              confirmLabel: 'Delete folder',
              body: project.formCount > 0
                ? `Its ${project.formCount} form${project.formCount === 1 ? '' : 's'} `
                  + 'will be moved out, not deleted.'
                : 'The folder is empty.',
            });

            if (ok) remove.mutate(project.id);
          }}
        />
      ))}

      <RailRow
        label="Unfiled"
        active={selected === 'none'}
        onClick={() => onSelect('none')}
      />
    </>
  );
}

function RailRow({
  label, count, colour, indent = 0, active, onClick, onDelete,
  renaming, onRename, onRenamed, onCancelRename,
}: {
  label: string;
  count?: number;
  colour?: string | null;
  indent?: number;
  active: boolean;
  onClick: () => void;
  onDelete?: () => void;
  renaming?: boolean;
  onRename?: () => void;
  onRenamed?: (value: string) => void;
  onCancelRename?: () => void;
}) {
  // Renamed in place rather than in a dialog. The name is one short string and it is
  // already on screen — a modal to change it would be more ceremony than the edit.
  if (renaming && onRenamed) {
    return (
      <form
        className="px-1 py-0.5"
        onSubmit={(e) => {
          e.preventDefault();
          const input = e.currentTarget.elements.namedItem('folder') as HTMLInputElement;
          onRenamed(input.value);
        }}
      >
        <input
          name="folder"
          defaultValue={label}
          autoFocus
          aria-label={`Rename ${label}`}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={(e) => onRenamed(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onCancelRename?.(); }}
          className="w-full rounded-md border border-[var(--ff-primary)] bg-transparent
                     px-2 py-1 text-[13px] outline-none"
        />
      </form>
    );
  }

  return (
    // The row controls are siblings of the row button, never nested inside it: a button
    // within a button is invalid and the inner one stops being reachable by keyboard.
    <div className="group flex items-center">
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        style={indent ? { paddingLeft: 10 + indent * 10 } : undefined}
        className={clsx(
          'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2.5 py-1.5 text-sm',
          'transition-colors',
          active
            ? 'bg-[var(--ff-surface-raised)] text-[var(--ff-text)] shadow-[inset_2px_0_0_var(--ff-accent)]'
            : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]',
        )}
      >
        {colour && (
          <span
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: colour }}
          />
        )}
        <span className="truncate">{label}</span>
        {count !== undefined && (
          <span className="ff-num ml-auto text-[12.5px] text-[var(--ff-text-faint)]">
            {count}
          </span>
        )}
      </button>

      {onRename && (
        <button
          type="button"
          aria-label={`Rename ${label}`}
          title="Rename"
          onClick={onRename}
          className="shrink-0 rounded px-1 text-[var(--ff-text-faint)] opacity-0
                     transition-opacity hover:text-[var(--ff-text)]
                     focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Icon name="pencil" size={12} />
        </button>
      )}

      {onDelete && (
        <button
          type="button"
          aria-label={`Delete ${label}`}
          title="Delete"
          onClick={onDelete}
          className="shrink-0 rounded px-1 text-[var(--ff-text-faint)] opacity-0
                     transition-opacity hover:text-[var(--ff-danger)]
                     focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Icon name="close" size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * Shown while the API is not answering.
 *
 * It names the time it went away and says plainly that the rows below are the last good
 * fetch — a stale list with no notice is the failure people act on without knowing.
 */
function OfflineBanner({
  since, stale, retrying, onRetry,
}: { since: Date | null; stale: boolean; retrying: boolean; onRetry: () => void }) {
  const time = since?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      role="status"
      className="mx-6 mt-5 flex flex-wrap items-center gap-2.5 rounded-[7px] border
                 border-[var(--ff-danger)]/35 bg-[var(--ff-danger-soft)] px-3.5 py-2.5
                 text-[13.5px]"
    >
      <span
        className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--ff-danger)]"
        aria-hidden="true"
      />
      <b className="font-medium">
        API unreachable{time ? ` since ${time}` : ''}.
      </b>
      <span className="text-[var(--ff-text-muted)]">
        {stale
          ? 'These rows come from the last successful sync.'
          : 'Nothing could be loaded.'}
      </span>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="ml-auto rounded-[5px] border border-[var(--ff-danger)]/40 px-2.5 py-1
                   text-[12.5px] text-[var(--ff-danger)] disabled:opacity-60
                   hover:bg-[var(--ff-danger)]/10"
      >
        {retrying ? 'Retrying…' : 'Retry now'}
      </button>
    </div>
  );
}

export function FormTabs() {
  const { formId } = useParams();
  const base = `/forms/${formId}`;

  const tabs: { to: string; label: string; icon: IconName; end?: boolean }[] = [
    { to: base, label: 'Build', icon: 'grid', end: true },
    { to: `${base}/logic`, label: 'Logic', icon: 'function' },
    { to: `${base}/outcomes`, label: 'Outcomes', icon: 'check' },
    { to: `${base}/share`, label: 'Share & embed', icon: 'link' },
    { to: `${base}/invites`, label: 'Single-use links', icon: 'ticket' },
    { to: `${base}/responses`, label: 'Responses', icon: 'checkbox' },
    { to: `${base}/analytics`, label: 'Dashboard', icon: 'gauge' },
    { to: `${base}/translations`, label: 'Translations', icon: 'globe' },
    { to: `${base}/settings`, label: 'Settings', icon: 'shield' },
  ];

  return (
    <nav
      className="flex shrink-0 gap-0.5 overflow-x-auto border-b border-[var(--ff-border)]
                 bg-[var(--ff-surface)] px-3"
      aria-label="Form sections"
    >
      <Link
        to="/"
        title="All forms"
        className="my-1.5 mr-1 flex items-center rounded-md px-2 text-[var(--ff-text-muted)]
                   hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]"
      >
        <Icon name="chevronLeft" size={16} />
      </Link>

      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) => clsx(
            'flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5',
            'text-sm transition-colors',
            isActive
              ? 'border-[var(--ff-accent)] font-medium text-[var(--ff-text)]'
              : 'border-transparent text-[var(--ff-text-muted)] hover:text-[var(--ff-text)]',
          )}
        >
          <Icon name={tab.icon} size={15} />
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}
